import React, { useCallback, useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import useMemoSelector from 'Core/Hooks/useMemoSelector'
import useMultiSlice from 'Core/Hooks/useMultiSlice'
import { deviceListSlice } from 'infra/redux/reducers'

import { ResponsiveTable, Typography } from 'procyon-ui'
import MagicLinkModal from 'Views/UsersAndGroups/UserDetails/Components/Overview/MagicLink/MagicLinkModal'
import SnackMessage from '../../Components/SnackMessage/SnackMessage'
import { prepareDevicesList } from '../../Utils/Helpers'
import { columns } from './DevicesTableMeta'
import { getUserNameById } from 'Utils/userUtils'

/* ------------------------------
 * Helpers (local, testable)
 * ------------------------------ */

const enrichDevicesWithUser = (devices, userList) =>
  devices.map((d) => {
    const resolved = getUserNameById(d?.UserName, userList)

    const resolvedName =
      typeof resolved === 'string' ? resolved : (resolved?.Spec?.Name ?? resolved?.name ?? '')

    return {
      ...d,
      UserId: d?.UserName,
      UserName: {
        label: resolvedName,
        userId: d?.UserName
      }
    }
  })

const filterDevicesBySearch = (devices, searchKey) => {
  const key = searchKey.trim().toLowerCase()
  if (!key) return devices

  return devices.filter((d) => {
    const deviceName = d?.Attributes?.DeviceName?.toLowerCase?.() ?? ''
    const userName = d?.UserName?.toLowerCase?.() ?? ''
    return deviceName.includes(key) || userName.includes(key)
  })
}

/* ------------------------------
 * Component
 * ------------------------------ */

const DeviceList = ({ viewMode = 'admin', tab = 'APPROVED', searchKey = '' }) => {
  const history = useHistory()
  const dispatch = useDispatch()

  const user = useSelector((state) => state.user)
  const userId = _.get(user, 'ObjectMeta.Name', '') // UUID
  const deviceInfo = useSelector((state) => state.user?.DeviceInfo)

  const { data: deviceListRaw } = useMemoSelector('deviceList')
  const deviceList = deviceListRaw ?? []

  const { slices } = useMultiSlice(['userList'])

  const [openSnack, setOpenSnack] = useState(false)
  const [openModal, setOpenModal] = useState(false)

  useEffect(() => {
    dispatch(deviceListSlice.thunk())
  }, [dispatch])

  const handleUserClick = useCallback(
    (userId) => {
      history.push(`/${viewMode}/users/${userId}`)
    },
    [history, viewMode]
  )

  const handleRowClick = useCallback(
    (data) => {
      history.push(`/${viewMode}/device/${data?.ID}`)
    },
    [history, viewMode]
  )

  const handleMagicLinkModalClose = useCallback(() => {
    setOpenModal(false)
  }, [])

  const preparedDeviceList = useMemo(() => {
    let devices = deviceList

    // Filter by user view (UUID safe)
    if (viewMode === 'user') {
      devices = devices.filter((d) => String(d?.UserName) === String(userId))
    }

    const enriched = enrichDevicesWithUser(devices, slices.userList)
    const searched = filterDevicesBySearch(enriched, searchKey)

    return prepareDevicesList(searched, deviceInfo)
  }, [deviceList, viewMode, userId, slices.userList, searchKey, deviceInfo])

  return (
    <div>
      <div>
        <Typography variant='h3'>Onboarded Device</Typography>
      </div>
      <div className='bg-[var(--color-common-bg-base)] mt-8 p-5'>
        <ResponsiveTable
          onRow={(data) => ({
            onClick: () => handleRowClick(data)
          })}
          columns={columns(handleUserClick)}
          data={preparedDeviceList}
        />

        <MagicLinkModal
          selectedUser={user}
          open={openModal}
          handleClose={handleMagicLinkModalClose}
        />

        <SnackMessage
          backgroundColor='secondary'
          setOpenSnack={setOpenSnack}
          openSnack={openSnack}
        />
      </div>
    </div>
  )
}

export default DeviceList
